
import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import MetricsCard from './MetricsCard';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useSLAPerformance } from '@/hooks/useSLAPerformance';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import ResponseTimeMetrics from './ResponseTimeMetrics';
import PriorityDistribution from './PriorityDistribution';
import TrendChart from './TrendChart';
import DebugMetrics from './DebugMetrics';
import ErrorBoundary from '@/components/ErrorBoundary';
import DashboardLoading from './DashboardLoading';
import SwapyDashboard from './SwapyDashboard';
import { getSLAStatus, getStatusColor, getUrgenciaColor } from '@/utils/slaStatus';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { EditRequisicaoDialog } from '@/components/Requisicoes/EditRequisicaoDialog';
import EditIncidenteDialog from '@/components/Incidentes/EditIncidenteDialog';
import SLARiskTicketsModal from './SLARiskTicketsModal';
import OpenTicketsModal from './OpenTicketsModal';
import ResolvedTodayModal from './ResolvedTodayModal';
import MyGroupTicketsModal from './MyGroupTicketsModal';
import { 
  Ticket, 
  Clock, 
  CheckCircle, 
  Users,
  AlertTriangle,
  TrendingUp,
  Calendar,
  Filter,
  ChevronLeft,
  ChevronRight,
  Settings
} from 'lucide-react';

const DashboardOverview = () => {
  const { tickets, stats, slaMetrics, isLoading } = useDashboardData();
  const {
    responseTimeMetrics,
    priorityDistribution,
    trendData,
    topCategories,
    topAnalysts,
    satisfactionMetrics,
    isLoading: metricsLoading
  } = useDashboardMetrics();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSLARiskModalOpen, setIsSLARiskModalOpen] = useState(false);
  const [isOpenTicketsModalOpen, setIsOpenTicketsModalOpen] = useState(false);
  const [isResolvedTodayModalOpen, setIsResolvedTodayModalOpen] = useState(false);
  const [isMyGroupTicketsModalOpen, setIsMyGroupTicketsModalOpen] = useState(false);
  const itemsPerPage = 3;

  if (isLoading || metricsLoading) {
    return <DashboardLoading />;
  }

  const getPriorityColor = (priority: string) => {
    return getUrgenciaColor(priority);
  };

  // Pagination logic
  const totalPages = Math.ceil(tickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTickets = tickets.slice(startIndex, startIndex + itemsPerPage);

  const handleTicketClick = (ticket: any) => {
    setSelectedTicket(ticket);
    setIsEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedTicket(null);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <section className="flex flex-col md:flex-row md:items-end md:justify-between animate-fade-in">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight gradient-heading">
            Visão Geral do Dashboard
          </h1>
        </div>
        <div className="mt-2 md:mt-0 text-sm text-gray-500 font-medium">
          Última atualização: {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
        </div>
      </section>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in" style={{ '--tw-animation-delay': '0.1s' } as React.CSSProperties}>
        <MetricsCard
          title="Tickets Abertos"
          value={stats?.totalOpen || 0}
          icon={Ticket}
          trend={{ value: "Atual", direction: "neutral" }}
          onClick={() => setIsOpenTicketsModalOpen(true)}
        />
        <MetricsCard
          title="SLA em Risco"
          value={stats?.slaAtRisk || 0}
          icon={Clock}
          trend={{ value: "Crítico", direction: "down" }}
          className="border-l-4 border-l-red-500"
          onClick={() => setIsSLARiskModalOpen(true)}
        />
        <MetricsCard
          title="Resolvidos Hoje"
          value={stats?.resolvedTodayCount || 0}
          icon={CheckCircle}
          trend={{ value: "Hoje", direction: "up" }}
          onClick={() => setIsResolvedTodayModalOpen(true)}
        />
        <MetricsCard
          title="Meus Tickets de Grupo"
          value={stats?.myGroupTicketsCount || 0}
          icon={Users}
          trend={{ value: "Meu Grupo", direction: "neutral" }}
          className="border-l-4 border-l-blue-500"
          onClick={() => setIsMyGroupTicketsModalOpen(true)}
        />
      </div>

      {/* Todos os Cards com Swapy */}
      <SwapyDashboard
        responseTimeMetrics={responseTimeMetrics}
        priorityDistribution={priorityDistribution}
        trendData={trendData}
        satisfactionMetrics={satisfactionMetrics}
        topCategories={topCategories}
        topAnalysts={topAnalysts}
        metricsLoading={metricsLoading}
        // Props adicionais para os cards fixos
        tickets={tickets}
        paginatedTickets={paginatedTickets}
        currentPage={currentPage}
        totalPages={totalPages}
        goToPrevPage={goToPrevPage}
        goToNextPage={goToNextPage}
        handleTicketClick={handleTicketClick}
        getPriorityColor={getPriorityColor}
        getStatusColor={getStatusColor}
        getSLAStatus={getSLAStatus}
        slaMetrics={slaMetrics}
        format={format}
        ptBR={ptBR}
      />

      {/* Dialogs */}
      {selectedTicket && (
        <>
          {selectedTicket.tipo === 'incidente' ? (
            <EditIncidenteDialog
              incidente={selectedTicket}
              isOpen={isEditDialogOpen}
              onClose={handleCloseDialog}
            />
          ) : (
            <EditRequisicaoDialog
              requisicao={selectedTicket}
              isOpen={isEditDialogOpen}
              onClose={handleCloseDialog}
            />
          )}
        </>
      )}

      {/* SLA Risk Modal */}
      <SLARiskTicketsModal
        isOpen={isSLARiskModalOpen}
        onClose={() => setIsSLARiskModalOpen(false)}
        onTicketClick={handleTicketClick}
      />

      {/* Open Tickets Modal */}
      <OpenTicketsModal
        isOpen={isOpenTicketsModalOpen}
        onClose={() => setIsOpenTicketsModalOpen(false)}
        onTicketClick={handleTicketClick}
      />

      {/* Resolved Today Modal */}
      <ResolvedTodayModal
        isOpen={isResolvedTodayModalOpen}
        onClose={() => setIsResolvedTodayModalOpen(false)}
        onTicketClick={handleTicketClick}
      />

      {/* My Group Tickets Modal */}
      <MyGroupTicketsModal
        isOpen={isMyGroupTicketsModalOpen}
        onClose={() => setIsMyGroupTicketsModalOpen(false)}
        onTicketClick={handleTicketClick}
      />
    </div>
  );
};

export default DashboardOverview;
